/**
 * E35 · the portal's side of Assessments 2 - the field lists the data provider asks for, the shapes it gets back, and the pure
 * helpers the hub tab and the backoffice form use. The rules themselves (the score, the answer count and range, the future date,
 * the relation, the definition's structure) are the deciders'; the portal only shapes what it sends. The portal takes no
 * dependency on `@cc/domain`, so the one scoring rule the in-session form shows (the same arithmetic the server recomputes and
 * refuses a mismatch of) is repeated here and pinned by `model.test.ts` against the domain's own table of expectations.
 */
import { ASSESSMENT_INSTRUMENT_IDS, TestType, type AssessmentInstrumentId } from '@cc/schema';

/** N-5 · the instruments the portal lists - read from the shared `TestType` catalogue (`@cc/schema`), never typed again here. */
export const INSTRUMENT_IDS: readonly AssessmentInstrumentId[] = ASSESSMENT_INSTRUMENT_IDS;
export type InstrumentId = AssessmentInstrumentId;

/** `AssessmentCreated.type` - the enum ordinal of each instrument (`TestType`). */
export const TYPE_OF: Record<InstrumentId, number> = Object.fromEntries(INSTRUMENT_IDS.map((id) => [id, TestType[id]])) as Record<
  InstrumentId,
  number
>;

export type Reading = { name: string; score: number; band: string | null; bandKey: string | null };
export type Change = { total: number; subscales: Array<{ name: string; change: number }> };
export type TrendRow = {
  source: 'app' | 'inSession' | 'external';
  subject: string;
  practiceId: string | null;
  instrumentId: string | null;
  instrumentName: string;
  assessmentId: string | null;
  externalAssessmentId: string | null;
  occurredAt: string;
  total: number;
  maxScore: number | null;
  subscales: Reading[];
  band: string | null;
  bandKey: string | null;
  bandIsWorstSubscale: boolean;
  changeSinceLast: Change | null;
  item9: { questionKey: string; answer: number | null; answerKey: string | null } | null;
  note: string | null;
};
export type Definition = {
  instrumentId: string;
  name: string;
  nameRu: string | null;
  itemsCount: number;
  responseScale: { min: number; max: number; labels: string[] };
  scoringRule: string;
  subscales: Array<{ name: string; items: number[]; multiplier: number | null; reverseItems: number[] | null }>;
  bands: Array<{ subscale: string | null; bands: Array<{ min: number; max: number; band: string }> }>;
  licenceClass: string;
  availableFor: string;
  languages: string[];
  itemsKey: string;
  citation: string;
};
export type Trend = { subject: string; instrumentId: string | null; definition: Definition | null; rows: TrendRow[] };

export const DEFINITION_FIELDS = [
  'instrumentId',
  'name',
  'nameRu',
  'itemsCount',
  { responseScale: ['min', 'max', 'labels'] },
  'scoringRule',
  { subscales: ['name', 'items', 'multiplier', 'reverseItems'] },
  { bands: ['subscale', { bands: ['min', 'max', 'band'] }] },
  'licenceClass',
  'availableFor',
  'languages',
  'itemsKey',
  'citation',
];

const READING_FIELDS = ['name', 'score', 'band', 'bandKey'];
export const TREND_FIELDS = [
  'subject',
  'instrumentId',
  { definition: DEFINITION_FIELDS },
  {
    rows: [
      'source',
      'subject',
      'practiceId',
      'instrumentId',
      'instrumentName',
      'assessmentId',
      'externalAssessmentId',
      'occurredAt',
      'total',
      'maxScore',
      { subscales: READING_FIELDS },
      'band',
      'bandKey',
      'bandIsWorstSubscale',
      { changeSinceLast: ['total', { subscales: ['name', 'change'] }] },
      { item9: ['questionKey', 'answer', 'answerKey'] },
      'note',
    ],
  },
];

export const COMMAND_FIELDS = ['id', 'recorded'];

/** The answers' arithmetic: sum; DASS-21 doubled; GDS-15 one point per symptom answer (a yes, or a no on a reverse-keyed item). */
export const scoreOf = (def: Definition, answers: ReadonlyArray<number | null>): number => {
  const n = def.itemsCount;
  const a = answers.slice(0, n).map((x) => (typeof x === 'number' ? x : 0));
  if (def.scoringRule === 'uricaReadiness') {
    // E49 · readiness = C + A + M - PC over the subscale MEANS, x 10, rounded - from the sums, so no float error leaks into the Int
    const meanOf = (name: string): number => {
      const s = def.subscales.find((x) => x.name === name);
      return s && s.items.length > 0 ? s.items.reduce((sum, item) => sum + (a[item - 1] ?? 0), 0) / s.items.length : 0;
    };
    const readiness = meanOf('contemplation') + meanOf('action') + meanOf('maintenance') - meanOf('precontemplation');
    return Math.round(readiness * 10);
  }
  if (def.scoringRule === 'reverseKeyedYesNo') {
    const reverse = new Set(def.subscales.flatMap((s) => s.reverseItems ?? []));
    return a.reduce((sum, x, i) => sum + (reverse.has(i + 1) ? (x === 0 ? 1 : 0) : x), 0);
  }
  const sum = a.reduce((s, x) => s + x, 0);
  return def.scoringRule === 'subscaleSumTimes2' ? sum * 2 : sum;
};

/** E49 · a score on record as the person reads it: the URICA's readiness is stored x 10 ("10.4" for 104); every other score is itself. */
export const SCORE_DIVISOR: Record<string, number> = { uricaReadiness: 10 };
export const scoreText = (scoringRule: string | null | undefined, value: number): string => {
  const d = SCORE_DIVISOR[scoringRule ?? ''] ?? 1;
  return d === 1 ? String(value) : (value / d).toFixed(1);
};
/** The scoring rule of a trend row's instrument (`urica` reads as readiness); the definition is the trend's own, else the row's id. */
export const ruleOfRow = (row: Pick<TrendRow, 'instrumentId'>, def: Pick<Definition, 'scoringRule'> | null | undefined): string | null =>
  def?.scoringRule ?? (row.instrumentId === 'urica' ? 'uricaReadiness' : null);

export const blankAnswers = (def: Definition): Array<number | null> => new Array(def.itemsCount).fill(null);
export const allAnswered = (answers: ReadonlyArray<number | null>): boolean =>
  answers.length > 0 && answers.every((a) => typeof a === 'number');

/** The i18n key of item `index` (0-based) / of a choice. */
export const itemKey = (def: Pick<Definition, 'itemsKey'>, index: number): string => `${def.itemsKey}.q${index + 1}`;
export const choicesOf = (def: Definition): Array<{ value: number; labelKey: string }> =>
  def.responseScale.labels.map((labelKey, i) => ({ value: def.responseScale.min + i, labelKey }));

/**
 * The chart's series, takes only (an external result has no scales), in time order: the total; and for an instrument whose total has
 * no band of its own (DASS-21) a line per scale instead - its total shares no range with them.
 */
export const seriesOf = (
  rows: TrendRow[],
  label: (key: string) => string,
  totalLabel: string,
): Array<{ key: string; label: string; points: Array<{ x: string; y: number }> }> => {
  const takes = rows.filter((r) => r.source !== 'external');
  const names = Array.from(new Set(takes.flatMap((r) => r.subscales.map((s) => s.name))));
  const byScale = names.length > 0 && takes.some((r) => r.bandIsWorstSubscale);
  if (byScale) {
    return names.map((name) => ({
      key: name,
      label: label(name),
      points: takes.map((r) => ({ x: r.occurredAt, y: r.subscales.find((s) => s.name === name)?.score ?? 0 })),
    }));
  }
  return [{ key: 'total', label: totalLabel, points: takes.map((r) => ({ x: r.occurredAt, y: r.total })) }];
};

/** A change line as a signed number: +6, -3, 0. */
export const signed = (n: number): string => (n > 0 ? `+${n}` : String(n));

// ── the backoffice form ──────────────────────────────────────────────────────────────────────────

export type BandDraft = { subscale: string | null; bands: Array<{ min: string; max: string; band: string }> };

export const bandDraftsOf = (def: Definition): BandDraft[] =>
  def.bands.map((row) => ({
    subscale: row.subscale,
    bands: row.bands.map((b) => ({ min: String(b.min), max: String(b.max), band: b.band })),
  }));

export const bandsInput = (drafts: BandDraft[]) =>
  drafts.map((row) => ({
    subscale: row.subscale,
    bands: row.bands.map((b) => ({ min: Number(b.min), max: Number(b.max), band: b.band.trim() })),
  }));

export const bandDraftsProblem = (drafts: BandDraft[]): boolean =>
  drafts.some((row) =>
    row.bands.some(
      (b) => b.min.trim() === '' || b.max.trim() === '' || Number.isNaN(Number(b.min)) || Number.isNaN(Number(b.max)) || !b.band.trim(),
    ),
  );

export const publishInputOf = (def: Definition, over: { name: string; nameRu: string; availableFor: string; citation: string }, bands: BandDraft[]) => ({
  instrumentId: def.instrumentId,
  name: over.name.trim(),
  nameRu: over.nameRu.trim() || null,
  itemsCount: def.itemsCount,
  responseScale: { min: def.responseScale.min, max: def.responseScale.max, labels: def.responseScale.labels },
  scoringRule: def.scoringRule,
  subscales: def.subscales.map((s) => ({
    name: s.name,
    items: s.items,
    ...(s.multiplier != null ? { multiplier: s.multiplier } : {}),
    ...(s.reverseItems?.length ? { reverseItems: s.reverseItems } : {}),
  })),
  bands: bandsInput(bands),
  licenceClass: def.licenceClass,
  availableFor: over.availableFor,
  languages: def.languages,
  itemsKey: def.itemsKey,
  citation: over.citation.trim(),
});
